import axios, { type AxiosInstance } from 'axios';
import { useAuth } from '../auth/store';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/v1';

/**
 * Admin API client. Every request carries the session token from
 * POST /admin/auth/login as `Authorization: Bearer <token>`; the API no
 * longer accepts the admin id (`X-Admin-Id`) as a credential. A 401 means
 * the session expired or was ended (logout, password reset, disabled), so
 * the local session is dropped and the app returns to the login page.
 */
export function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: BASE_URL,
    timeout: 15_000,
    headers: { 'content-type': 'application/json' },
  });

  instance.interceptors.request.use((config) => {
    const token = useAuth.getState().token;
    if (token) {
      config.headers.set('authorization', `Bearer ${token}`);
    }
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    (err) => {
      if (err?.response?.status === 401) {
        useAuth.getState().logout();
      }
      return Promise.reject(err);
    },
  );

  return instance;
}

export const api = createApiClient();
